import { Component, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';


import { HisColumnMetadata } from '../model/his-column-metadata';
import { HisSearchResult, HisSearchResultRow, HisSearchResultSelectedRow } from '../model/his-search-result';
import { HisSearchService } from '../service/his-search.service';

import { HisSearch, HisSearchCriteria, HisSearchGroup } from '../model/search/his-search';

import cloneDeep from 'lodash/cloneDeep';

import { differenceWith } from 'lodash';
import { SortEvent } from 'primeng/api';
import { Observable, forkJoin, of, tap } from 'rxjs';
import { HisNavigationService } from '../../core/service/base/his-navigation.service';
import { HisUiService } from '../../core/service/base/his-ui.service';
import { GenericSearchUtil } from '../utils/generic-search-util';

@Component( {
    selector: 'his-search-result-selected-items',
    templateUrl: './his-search-result-selected-items.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
} )
export class HisSearchResultSelectedItemsComponent {
    private readonly DEFAULT_PAGE_SIZE = 10;

    private readonly searchService = inject( HisSearchService );
    private readonly uiService = inject( HisUiService );
    private readonly navigationService = inject( HisNavigationService );

    /** The search object used for the result table. */
    readonly currentSearch = input.required<HisSearch>();
    /** The currently selected options in the result table. */
    readonly selectedOptions = input.required<HisSearchResultSelectedRow[]>();
    /** The column used to identify individual options. */
    readonly selectedOptionsIdColumn = input.required<string>();
    /** The column used to display individual options. */
    readonly selectedOptionsLabelColumn = input.required<string>();
    /** The columns displayed in the result table. */
    readonly columns = input.required<HisColumnMetadata[]>();
    readonly idColumn = input.required<string>();

    /** Emits all rows that have been deselected from the overlay. */
    readonly deselectedOptions = output<HisSearchResultRow[]>();

    // overlay
    protected readonly overlayRows = signal<HisSearchResultRow[]>( [] );
    // Currently checked rows in the overlay table
    protected readonly overlaySelection = signal<HisSearchResultRow[]>( [] );
    protected readonly displayOverlay = signal<boolean>( false );

    // Pagination properties
    protected readonly rowsPerPageOverlay = this.DEFAULT_PAGE_SIZE;
    protected readonly rowsPerPageOptionsOverlay = [10, 15, 25, 50, 100, 150, 200, 250, 300];

    protected onOpenOverlay() {
        this.loadSelectedRows().subscribe( () => {
            this.displayOverlay.set( true );
        } );
    }

    private loadSelectedRows(): Observable<HisSearchResult[]> {
        // Don't load if there are no selected options
        if ( !this.selectedOptions().length ) {
            this.resetOverlayData();
            return of( null );
        }

        const searchRequests = this.createSearchRequestsFromSelectedOptions( this.currentSearch() );

        return forkJoin( searchRequests.map( req => this.loadSearchResult( req ) ) ).pipe( tap( ( results: HisSearchResult[] ) => {
            const parsedRows = results.flatMap( result => this.parseResultRows( result.data ) );
            this.overlayRows.set( [...parsedRows] );
            this.overlaySelection.set( [...parsedRows] );
        } ) );
    }

    /**
     * Parses the result rows to ensure that the idIdx and rowKey are correctly set based on the selectedOptionsIdColumn.
     */
    private parseResultRows( resultRows: HisSearchResultRow[] ) {
        if ( !resultRows || !resultRows.length ) {
            return [];
        }
        const probeRow = resultRows[0];
        const idColIdx = probeRow.getIndex( this.selectedOptionsIdColumn() );
        if ( idColIdx === null || idColIdx === probeRow.idIdx ) {
            return [...resultRows];
        }
        return resultRows.map( row => {
            row.idIdx = idColIdx;
            row.rowKey = String( row.values[idColIdx] );
            return row;
        } );
    }

    protected handleSort( sortEvent: SortEvent ) {
        const sortMeta = sortEvent.multiSortMeta;
        GenericSearchUtil.sortDataInPlace( sortEvent.data, sortMeta );
    }

    /**
     * Creates one or more clones of the given HisSearch to be used in the
     * 'already selected' overlay. Each search holds a single criteria with a
     * comma-separated list of selected option IDs. The list is split across
     * multiple searches whenever the accumulated value string would exceed
     * the maximum criteria value length of 3000 characters.
     * 
     * Each ID is prefixed with an equals sign to ensure exact matching in the resulting SQL
     * (e.g. `=123` instead of `LIKE %123%` which could falsely match 1234 or 0123 aswell)
     */
    private createSearchRequestsFromSelectedOptions( hisSubSearch: HisSearch ): HisSearch[] {
        const MAX_CRITERIA_VALUE_LENGTH = 3000;
        const searches: HisSearch[] = [];
        let currentChunk = '';

        for ( const option of this.selectedOptions() ) {
            const id = String( option.id );
            // Each ID is prefixed with an equals sign to ensure exact matching in the resulting SQL
            // (e.g. =123 instead of LIKE %123% which could match 1234 or 0123)
            const appended = currentChunk.length === 0 ? `=${id}` : `${currentChunk},=${id}`;

            if ( appended.length > MAX_CRITERIA_VALUE_LENGTH ) {
                // Flush the current chunk as its own search and start a new one
                searches.push( this.buildSearchForChunk( hisSubSearch, currentChunk ) );
                currentChunk = `=${id}`;
            } else {
                currentChunk = appended;
            }
        }

        if ( currentChunk.length > 0 ) {
            searches.push( this.buildSearchForChunk( hisSubSearch, currentChunk ) );
        }

        return searches;
    }

    /**
     * Builds a search request for a single chunk of comma-separated option IDs.
     */
    private buildSearchForChunk( hisSubSearch: HisSearch, chunkValue: string ): HisSearch {
        const clonedSearch = cloneDeep( hisSubSearch );
        clonedSearch.groups = [];

        const group = new HisSearchGroup();
        group.groupLabel = this.selectedOptionsIdColumn();
        group.criteriaGroupAnd = false;
        group.criteriaGroups = [];

        const criteria: HisSearchCriteria = { field: this.selectedOptionsIdColumn(), value: chunkValue, not: undefined, operator: undefined };
        group.criteria = [criteria];

        clonedSearch.groups.push( group );
        return clonedSearch;
    }

    /**
     * Loads pre and selected items.
     */
    private loadSearchResult( search: HisSearch ): Observable<HisSearchResult> {
        return this.searchService.loadSearchResult( search, 0, this.selectedOptions().length, null );
    }

    private resetOverlayData(): void {
        this.overlayRows.set( [] );
        this.overlaySelection.set( [] );
    }

    /**
     * Gets tooltip text for row selection
     */
    protected getTableCheckboxTitle( row: HisSearchResultRow ): string {
        const isSelected = this.overlaySelection().some( selectedRow => selectedRow.rowKey == row.rowKey );

        const labelIdx = row.getIndex( this.selectedOptionsLabelColumn() ) ?? 0;
        const displayValue = row.getAt( labelIdx );

        if ( isSelected ) {
            return this.uiService.translate( 'common.deselectOf', displayValue );
        }

        return this.uiService.translate( 'genericSearch.selectItemOf', displayValue );
    }

    /**
     * Stores last URL before navigation
     */
    protected onBeforeNavigateDefaultRoute() {
        this.navigationService.setLastBackUrl();
    }

    /**
     * Updates selected items based on overlay selection
     */
    protected onUpdateTableSelectItems() {
        const rowsToDeselect = differenceWith( this.overlayRows(), this.overlaySelection(), (a, b) => a.rowKey === b.rowKey );
        this.deselectedOptions.emit( rowsToDeselect );

        this.displayOverlay.set( false );
    }

    /**
     * Cancels any changes made in the overlay and reverts to the previous selection state.
     */
    protected onCancelOverlay() {
        this.displayOverlay.set( false );
    }
}
